import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./WealthPage.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { PARTNER_OFFERINGS } from "../../data/partnerOfferings";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";

const PILLAR_LEAD_MODULES = {
  "investment-strategy": WEB_LEAD_MODULES.WEALTH_INV,
  "wealth-management": WEB_LEAD_MODULES.WEALTH_WM,
  "growth-allocation": WEB_LEAD_MODULES.WEALTH_GA,
  mcx: WEB_LEAD_MODULES.WEALTH_MCX,
};

/* ── Scroll reveal hook ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

/* ════════════════════════════════════════════
   DATA
════════════════════════════════════════════ */
const pillars = [
  {
    id: "investment-strategy",
    icon: "fa-chart-line",
    accent: "#2563eb",
    accentEnd: "#0891b2",
    tag: "Equity · Mutual Funds · SIP",
    title: "Investment Strategy",
    tagline: "Customised portfolios built around your goals, not ours",
    description:
      "We design investment strategies that are deeply personal — calibrated to your income, risk appetite, time horizon, and life milestones. Whether you prefer equity, debt, or a hybrid approach, every rupee is deployed with intent.",
    points: [
      {
        icon: "fa-sliders",
        label: "Risk-Profiled Allocation",
        desc: "Conservative to aggressive — we match strategy to your temperament.",
      },
      {
        icon: "fa-bullseye",
        label: "Goal-Linked Portfolios",
        desc: "Each investment tied to a specific goal: home, retirement, education.",
      },
      {
        icon: "fa-rotate",
        label: "Dynamic Rebalancing",
        desc: "Portfolios reviewed and adjusted quarterly to stay on track.",
      },
      {
        icon: "fa-shield",
        label: "Downside Protection",
        desc: "Tactical hedges and asset mix to cushion market downturns.",
      },
    ],
    stat1: { val: "₹100Cr+", lbl: "AUM Managed" },
    stat2: { val: "800+", lbl: "Active Portfolios" },
  },
  {
    id: "wealth-management",
    icon: "fa-briefcase",
    accent: "#0d7a5f",
    accentEnd: "#059669",
    tag: "HNI · Family Office · PMS",
    title: "Wealth Management",
    tagline:
      "Comprehensive oversight for individuals and families who mean business",
    description:
      "Wealth management at Wealth Compounders goes beyond picking stocks. We integrate investment planning, tax efficiency, estate structuring, and succession advisory into a unified wealth roadmap for high-net-worth clients.",
    points: [
      {
        icon: "fa-layer-group",
        label: "Multi-Asset Strategy",
        desc: "Equities, debt, real estate, gold and alternatives — all coordinated.",
      },
      {
        icon: "fa-file-invoice-dollar",
        label: "Tax Optimisation",
        desc: "Smart structuring to minimise your tax burden legally and efficiently.",
      },
      {
        icon: "fa-users",
        label: "Family Financial Planning",
        desc: "Inheritance, succession, and wealth transfer across generations.",
      },
      {
        icon: "fa-chart-pie",
        label: "Consolidated Reporting",
        desc: "One dashboard for all your holdings, performance, and net worth.",
      },
    ],
    stat1: { val: "20+", lbl: "Years Experience" },
    stat2: { val: "99%", lbl: "Client Satisfaction" },
  },
  {
    id: "growth-allocation",
    icon: "fa-chart-pie",
    accent: "#7c3aed",
    accentEnd: "#6d28d9",
    tag: "Asset Allocation · SWP · STP",
    title: "Growth & Allocation",
    tagline:
      "Precision allocation strategies that compound wealth over the long run",
    description:
      "Smart wealth growth is not just about picking the right fund — it's about how capital is distributed across asset classes, time horizons, and market cycles. Our allocation frameworks are built to maximise compounding while managing volatility.",
    points: [
      {
        icon: "fa-code-branch",
        label: "Core-Satellite Approach",
        desc: "Stable core holdings anchored by high-growth satellite positions.",
      },
      {
        icon: "fa-arrow-trend-up",
        label: "Systematic Transfer Plans",
        desc: "STPs to move lumpsum capital into equity gradually and safely.",
      },
      {
        icon: "fa-money-bill-transfer",
        label: "Systematic Withdrawal",
        desc: "SWP strategies for regular income without depleting your corpus.",
      },
      {
        icon: "fa-hourglass-half",
        label: "Long-Term Compounding",
        desc: "Stay invested strategies harnessing the full power of time.",
      },
    ],
    stat1: { val: "57%", lbl: "Avg. Portfolio ROI" },
    stat2: { val: "10+", lbl: "Yrs Avg. Client Tenure" },
  },
  {
    id: "mcx",
    icon: "fa-coins",
    accent: "#d97706",
    accentEnd: "#b45309",
    tag: "Gold · Crude · Silver · Agri",
    title: "MCX Commodity",
    tagline:
      "Navigate volatile commodity markets with precision and confidence",
    description:
      "MCX markets offer powerful diversification and trading opportunities — but require specialised knowledge to trade profitably. Our expert MCX advisory covers gold, silver, crude oil, and agri-commodities with real-time analysis and risk-mapped entries.",
    points: [
      {
        icon: "fa-circle-nodes",
        label: "Multi-Commodity Coverage",
        desc: "Gold, silver, crude oil, natural gas, and key agri-commodities.",
      },
      {
        icon: "fa-crosshairs",
        label: "Entry & Exit Precision",
        desc: "Every call comes with target, stop-loss and position sizing.",
      },
      {
        icon: "fa-clock-rotate-left",
        label: "Intraday & Positional",
        desc: "Short-term intraday plays and longer positional swing trades.",
      },
      {
        icon: "fa-newspaper",
        label: "Daily Market Reports",
        desc: "Pre-market outlook and post-market analysis every trading day.",
      },
    ],
    stat1: { val: "Daily", lbl: "Market Reports" },
    stat2: { val: "Live", lbl: "Commodity Calls" },
  },
];

const process = [
  {
    step: "01",
    icon: "fa-comments",
    title: "Discovery Call",
    desc: "We understand your financial situation, goals, and risk tolerance in a free 30-minute consultation.",
  },
  {
    step: "02",
    icon: "fa-pen-ruler",
    title: "Strategy Design",
    desc: "A personalised wealth plan is crafted — covering allocation, instruments, timelines, and milestones.",
  },
  {
    step: "03",
    icon: "fa-rocket",
    title: "Execution",
    desc: "We implement the strategy across platforms like NJ Wealth with full transparency.",
  },
  {
    step: "04",
    icon: "fa-rotate",
    title: "Review & Rebalance",
    desc: "Quarterly reviews ensure your portfolio stays aligned with goals as markets and life evolve.",
  },
];

/*COMPONENTS*/

const PillarCard = ({ pillar, index }) => {
  const [ref, inView] = useInView(0.08);
  const navigate = useNavigate();
  const isFlipped = index % 2 !== 0;

  return (
    <article
      ref={ref}
      id={pillar.id}
      className={`wp-pillar ${inView ? "wp-pillar--in" : ""} ${isFlipped ? "wp-pillar--flip" : ""}`}
      style={{ transitionDelay: "0ms" }}
    >
      {/* Top accent bar */}
      <div
        className="wp-pillar-bar"
        style={{
          background: `linear-gradient(90deg, ${pillar.accent}, ${pillar.accentEnd})`,
        }}
      />

      <div className="wp-pillar-inner">
        {/* VISUAL PANEL */}
        <div className="wp-pillar-visual">
          <div
            className="wp-pillar-vis-bg"
            style={{
              background: `linear-gradient(135deg, ${pillar.accent}0e, ${pillar.accentEnd}08)`,
            }}
          />
          <div
            className="wp-pillar-dots"
            style={{
              backgroundImage: `radial-gradient(${pillar.accent}1a 1.5px, transparent 1.5px)`,
            }}
          />

          {/* Big icon orb */}
          <div className="wp-pillar-orb-wrap">
            <div
              className="wp-pillar-orb-ring wp-orb-r1"
              style={{ borderColor: `${pillar.accent}20` }}
            />
            <div
              className="wp-pillar-orb-ring wp-orb-r2"
              style={{ borderColor: `${pillar.accent}12` }}
            />
            <div
              className="wp-pillar-orb"
              style={{
                background: `linear-gradient(135deg, ${pillar.accent}, ${pillar.accentEnd})`,
              }}
            >
              <i className={`fas ${pillar.icon}`} />
            </div>
          </div>

          {/* Two stat bubbles */}
          <div
            className="wp-pillar-stat wp-stat-1"
            style={{ borderColor: `${pillar.accent}20` }}
          >
            <span
              className="wp-stat-val"
              style={{
                background: `linear-gradient(135deg, ${pillar.accent}, ${pillar.accentEnd})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {pillar.stat1.val}
            </span>
            <span className="wp-stat-lbl">{pillar.stat1.lbl}</span>
          </div>
          <div
            className="wp-pillar-stat wp-stat-2"
            style={{ borderColor: `${pillar.accent}20` }}
          >
            <span
              className="wp-stat-val"
              style={{
                background: `linear-gradient(135deg, ${pillar.accent}, ${pillar.accentEnd})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {pillar.stat2.val}
            </span>
            <span className="wp-stat-lbl">{pillar.stat2.lbl}</span>
          </div>
        </div>

        {/* CONTENT PANEL */}
        <div className="wp-pillar-content">
          <div
            className="wp-pillar-tag"
            style={{
              color: pillar.accent,
              background: `${pillar.accent}10`,
              border: `1px solid ${pillar.accent}20`,
            }}
          >
            {pillar.tag}
          </div>

          <h2 className="wp-pillar-title">{pillar.title}</h2>
          <p className="wp-pillar-tagline">{pillar.tagline}</p>
          <p className="wp-pillar-desc">{pillar.description}</p>

          {/* Feature grid */}
          <div className="wp-pillar-points">
            {pillar.points.map((pt, i) => (
              <div key={i} className="wp-point">
                <div
                  className="wp-point-icon"
                  style={{
                    background: `${pillar.accent}12`,
                    color: pillar.accent,
                    border: `1px solid ${pillar.accent}1e`,
                  }}
                >
                  <i className={`fas ${pt.icon}`} />
                </div>
                <div>
                  <p className="wp-point-label">{pt.label}</p>
                  <p className="wp-point-desc">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            className="wp-pillar-btn"
            style={{
              background: `linear-gradient(135deg, ${pillar.accent}, ${pillar.accentEnd})`,
            }}
            onClick={() =>
              navigate(
                getConsultationPath(
                  PILLAR_LEAD_MODULES[pillar.id] || WEB_LEAD_MODULES.WEALTH,
                ),
              )
            }
          >
            Get Started <i className="fas fa-arrow-right" />
          </button>
        </div>
      </div>
    </article>
  );
};

const ProcessStep = ({ step, icon, title, desc, delay, inView }) => (
  <div
    className={`wp-step ${inView ? "wp-step--in" : ""}`}
    style={{ transitionDelay: `${delay}ms` }}
  >
    <div className="wp-step-num">{step}</div>
    <div className="wp-step-icon">
      <i className={`fas ${icon}`} />
    </div>
    <h4 className="wp-step-title">{title}</h4>
    <p className="wp-step-desc">{desc}</p>
    <div className="wp-step-connector" />
  </div>
);

const PartnerOfferingCard = ({ partner }) => (
  <article className="wp-partner-card">
    <div className="wp-partner-orb" />
    <div className="wp-partner-card-head">
      <div className="wp-partner-brand">
        <div className="wp-partner-logo-wrap">
          <img src={partner.logo} alt={`${partner.name} logo`} className="wp-partner-logo" />
        </div>
        <h3>{partner.name}</h3>
      </div>
      <span>{partner.subtitle}</span>
    </div>
    <p className="wp-partner-intro">{partner.intro}</p>
    <ul className="wp-partner-highlights">
      {partner.quickHighlights.map((item) => (
        <li key={`${partner.key}-${item}`}>{item}</li>
      ))}
    </ul>
    <Link className="wp-partner-btn" to={partner.route}>
      View Full Offering <i className="fas fa-arrow-right" />
    </Link>
  </article>
);

/*PAGE */
const WealthPage = () => {
  const navigate = useNavigate();
  const [heroRef, heroInView] = useInView(0.1);
  const [processRef, processInView] = useInView(0.1);
  const [ctaRef, ctaInView] = useInView(0.1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wp-wrapper">
      <Navbar />

      {/* ══ HERO ══ */}
      <section className="wp-hero" ref={heroRef}>
        <div className="wp-hero-mesh" />
        <div className="wp-hero-dots" />
        <div className="wp-hero-glow wp-glow-1" />
        <div className="wp-hero-glow wp-glow-2" />

        <div className="wp-container wp-hero-inner">
          <div className="wp-hero-cols">
            {/* LEFT: text */}
            <div
              className={`wp-hero-text ${heroInView ? "wp-hero-text--in" : ""}`}
            >
              <div className="wp-hero-eyebrow">
                <span className="wp-eyebrow-dot" />
                How We Multiply Your Wealth
              </div>
              <h1 className="wp-hero-h1">
                Every Rupee,
                <br />
                <em className="wp-em">Working Harder</em>
                <br />
                For You
              </h1>
              <p className="wp-hero-sub">
                Four proven wealth-building pillars — each designed to work
                independently or as part of a complete, integrated financial
                strategy tailored to your life.
              </p>
              {/* <div className="wp-hero-nav">
                {pillars.map((p) => (
                  <a
                    key={p.id}
                    href={`#${p.id}`}
                    className="wp-hero-nav-chip"
                    style={{ "--c": p.accent }}
                  >
                    <span
                      className="wp-nav-dot"
                      style={{ background: p.accent }}
                    />
                    {p.title}
                  </a>
                ))}
              </div> */}
            </div>

            {/* RIGHT: isometric illustration */}
            <div
              className={`wp-hero-visual ${heroInView ? "wp-hero-visual--in" : ""}`}
            >
              {/* Floating badge cards */}
              <div className="wp-iso-badge wp-iso-badge--1">
                <div
                  className="wp-iso-badge-icon"
                  style={{
                    background: "linear-gradient(135deg,#2563eb,#0891b2)",
                  }}
                >
                  <i className="fas fa-arrow-trend-up" />
                </div>
                <div>
                  <p className="wp-iso-badge-val">+57%</p>
                  <p className="wp-iso-badge-lbl">Portfolio ROI</p>
                </div>
              </div>

              <div className="wp-iso-badge wp-iso-badge--2">
                <div
                  className="wp-iso-badge-icon"
                  style={{
                    background: "linear-gradient(135deg,#059669,#0d7a5f)",
                  }}
                >
                  <i className="fas fa-shield-halved" />
                </div>
                <div>
                  <p className="wp-iso-badge-val">SEBI</p>
                  <p className="wp-iso-badge-lbl">Registered</p>
                </div>
              </div>

              <div className="wp-iso-badge wp-iso-badge--3">
                <div
                  className="wp-iso-badge-icon"
                  style={{
                    background: "linear-gradient(135deg,#d97706,#b45309)",
                  }}
                >
                  <i className="fas fa-coins" />
                </div>
                <div>
                  <p className="wp-iso-badge-val">₹100Cr+</p>
                  <p className="wp-iso-badge-lbl">Assets Managed</p>
                </div>
              </div>

              {/* Central isometric SVG scene */}
              <svg
                className="wp-iso-svg"
                viewBox="0 0 460 400"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="isoBlue" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#0891b2" />
                  </linearGradient>
                  <linearGradient id="isoGreen" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#059669" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                  <linearGradient id="isoPurple" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#7c3aed" />
                    <stop offset="100%" stopColor="#6d28d9" />
                  </linearGradient>
                  <linearGradient id="isoAmber" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#d97706" />
                    <stop offset="100%" stopColor="#f59e0b" />
                  </linearGradient>
                  <linearGradient
                    id="isoShadowBlue"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#1e40af" stopOpacity="0.7" />
                  </linearGradient>
                  <linearGradient
                    id="isoShadowGreen"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#047857" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#065f46" stopOpacity="0.7" />
                  </linearGradient>
                  <linearGradient
                    id="isoShadowPurple"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#5b21b6" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#4c1d95" stopOpacity="0.7" />
                  </linearGradient>
                  <linearGradient
                    id="isoShadowAmber"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#b45309" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#92400e" stopOpacity="0.7" />
                  </linearGradient>
                  <linearGradient id="isoPlatform" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#e0eaff" />
                    <stop offset="100%" stopColor="#c7d8ff" />
                  </linearGradient>
                  <filter id="isoDropShadow">
                    <feDropShadow
                      dx="0"
                      dy="6"
                      stdDeviation="10"
                      floodColor="#2563eb"
                      floodOpacity="0.15"
                    />
                  </filter>
                </defs>

                {/* Platform base shadow */}
                <ellipse
                  cx="230"
                  cy="346"
                  rx="168"
                  ry="30"
                  fill="#2563eb"
                  opacity="0.07"
                />

                {/*  Bar 1 — Blue (Investment Strategy, tallest) */}
                <polygon
                  points="108,198 140,180 140,312 108,330"
                  fill="url(#isoShadowBlue)"
                />
                <polygon
                  points="140,180 172,198 172,319 140,312"
                  fill="#1d4ed8"
                  opacity="0.72"
                />
                <polygon
                  points="108,198 140,180 172,198 140,216"
                  fill="url(#isoBlue)"
                />
                <polyline
                  points="114,204 122,198 130,202 140,194 150,199 158,193 166,197"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <text
                  x="140"
                  y="173"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill="#2563eb"
                  opacity="0.7"
                >
                  SIP
                </text>

                {/*  Bar 2 — Green (Wealth Management) */}
                <polygon
                  points="158,222 190,204 190,312 158,330"
                  fill="url(#isoShadowGreen)"
                />
                <polygon
                  points="190,204 222,222 222,319 190,312"
                  fill="#047857"
                  opacity="0.72"
                />
                <polygon
                  points="158,222 190,204 222,222 190,240"
                  fill="url(#isoGreen)"
                />
                <polyline
                  points="164,228 172,222 180,226 190,218 200,223 208,217 216,221"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <text
                  x="190"
                  y="197"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill="#059669"
                  opacity="0.7"
                >
                  HNI
                </text>

                {/* Bar 3 — Purple (Growth & Allocation) */}
                <polygon
                  points="208,244 240,226 240,312 208,330"
                  fill="url(#isoShadowPurple)"
                />
                <polygon
                  points="240,226 272,244 272,319 240,312"
                  fill="#5b21b6"
                  opacity="0.72"
                />
                <polygon
                  points="208,244 240,226 272,244 240,262"
                  fill="url(#isoPurple)"
                />
                <polyline
                  points="214,250 222,244 230,248 240,240 250,245 258,239 266,243"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <text
                  x="240"
                  y="219"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill="#7c3aed"
                  opacity="0.7"
                >
                  SWP
                </text>

                {/*  Bar 4 — Amber (MCX) */}
                <polygon
                  points="258,263 290,245 290,312 258,330"
                  fill="url(#isoShadowAmber)"
                />
                <polygon
                  points="290,245 322,263 322,319 290,312"
                  fill="#b45309"
                  opacity="0.72"
                />
                <polygon
                  points="258,263 290,245 322,263 290,281"
                  fill="url(#isoAmber)"
                />
                <polyline
                  points="264,269 272,263 280,267 290,259 300,264 308,258 316,262"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.65"
                />
                <text
                  x="290"
                  y="238"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="800"
                  fill="#d97706"
                  opacity="0.7"
                >
                  MCX
                </text>

                {/* Dashed trend line connecting bar tops */}
                <polyline
                  points="140,180 190,204 240,226 290,245"
                  stroke="url(#isoBlue)"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  opacity="0.3"
                />

                {/*  Coin stack (right side) */}
                <ellipse
                  cx="372"
                  cy="242"
                  rx="27"
                  ry="8"
                  fill="#f59e0b"
                  opacity="0.85"
                />
                <rect
                  x="345"
                  y="232"
                  width="54"
                  height="12"
                  rx="2"
                  fill="#d97706"
                />
                <ellipse cx="372" cy="232" rx="27" ry="8" fill="#fbbf24" />

                <ellipse
                  cx="372"
                  cy="222"
                  rx="23"
                  ry="7"
                  fill="#f59e0b"
                  opacity="0.85"
                />
                <rect
                  x="349"
                  y="213"
                  width="46"
                  height="11"
                  rx="2"
                  fill="#d97706"
                />
                <ellipse cx="372" cy="213" rx="23" ry="7" fill="#fcd34d" />

                <ellipse
                  cx="372"
                  cy="204"
                  rx="19"
                  ry="6"
                  fill="#f59e0b"
                  opacity="0.85"
                />
                <rect
                  x="353"
                  y="196"
                  width="38"
                  height="10"
                  rx="2"
                  fill="#d97706"
                />
                <ellipse cx="372" cy="196" rx="19" ry="6" fill="#fde68a" />
                <text
                  x="372"
                  y="200"
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="900"
                  fill="#78350f"
                  opacity="0.85"
                >
                  ₹
                </text>

                {/* Pie donut (top-right)*/}
                <g transform="translate(400,155)" filter="url(#isoDropShadow)">
                  <circle cx="0" cy="0" r="32" fill="white" opacity="0.96" />
                  <path d="M0,0 L0,-30 A30,30 0 0,1 26,15 Z" fill="#2563eb" />
                  <path d="M0,0 L26,15 A30,30 0 0,1 -15,26 Z" fill="#10b981" />
                  <path d="M0,0 L-15,26 A30,30 0 0,1 -30,0 Z" fill="#7c3aed" />
                  <path d="M0,0 L-30,0 A30,30 0 0,1 0,-30 Z" fill="#f59e0b" />
                  <circle cx="0" cy="0" r="15" fill="white" />
                  <text
                    x="0"
                    y="5"
                    textAnchor="middle"
                    fontSize="9"
                    fontWeight="900"
                    fill="#0f172a"
                  >
                    4x
                  </text>
                </g>

                {/* Growth arrow (top-left)*/}
                <g transform="translate(76,148)">
                  <circle cx="0" cy="0" r="24" fill="#2563eb" opacity="0.1" />
                  <circle cx="0" cy="0" r="17" fill="#2563eb" opacity="0.15" />
                  <path
                    d="M-5,7 L0,-8 L5,7"
                    stroke="url(#isoBlue)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <line
                    x1="0"
                    y1="-8"
                    x2="0"
                    y2="11"
                    stroke="url(#isoBlue)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </g>

                {/* ── Sparkle/particle dots ── */}
                {[
                  [385, 152],
                  [58, 282],
                  [338, 126],
                  [425, 295],
                  [90, 172],
                  [340, 320],
                  [155, 150],
                ].map(([cx, cy], i) => (
                  <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={i % 2 === 0 ? 3 : 2}
                    fill={
                      [
                        "#2563eb",
                        "#10b981",
                        "#d97706",
                        "#7c3aed",
                        "#0891b2",
                        "#f59e0b",
                        "#2563eb",
                      ][i]
                    }
                    opacity="0.45"
                  >
                    <animate
                      attributeName="opacity"
                      values="0.2;0.7;0.2"
                      dur={`${1.6 + i * 0.35}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="r"
                      values={i % 2 === 0 ? "2;4;2" : "1.5;2.8;1.5"}
                      dur={`${1.6 + i * 0.35}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                ))}
              </svg>
            </div>
          </div>

          {/* Stats strip*/}
          {/* <div
            className={`wp-hero-stats ${heroInView ? "wp-hero-stats--in" : ""}`}
          >
            {[
              { val: "₹100Cr+", lbl: "Assets Managed" },
              { val: "800+", lbl: "Happy Clients" },
              { val: "20+", lbl: "Years Experience" },
              { val: "99%", lbl: "Satisfaction" },
            ].map((s) => (
              <div className="wp-hero-stat" key={s.lbl}>
                <span className="wp-hero-stat-val">{s.val}</span>
                <span className="wp-hero-stat-lbl">{s.lbl}</span>
              </div>
            ))}
          </div> */}
        </div>
      </section>

      {/* PILLARS */}
      {/* <section className="wp-pillars">
        <div className="wp-container">
          <div className="wp-section-eyebrow">
            <span />
            <span>Our Four Pillars</span>
            <span />
          </div>
          {pillars.map((p, i) => (
            <PillarCard key={p.id} pillar={p} index={i} />
          ))}
        </div>
      </section> */}

      <section className="wp-partners-offering">
        <div className="wp-container">
          <div className="wp-section-eyebrow">
            <span />
            <span>Partner Ecosystem</span>
            <span />
          </div>
          <h2 className="wp-partners-title">
            Our Partner&apos;s <em className="wp-em">Offering</em>
          </h2>
          <p className="wp-partners-sub">
            Get a quick overview of our partner platforms. Explore each page for
            complete catalogs, product categories, and detailed offerings.
          </p>
          <div className="wp-partner-grid">
            {Object.values(PARTNER_OFFERINGS)
              // Motilal Oswal hidden for now — re-add by removing this filter
              .filter((partner) => partner.key !== "motilal")
              .map((partner) => (
                <PartnerOfferingCard key={partner.name} partner={partner} />
              ))}
          </div>
        </div>
      </section>

      {/*OUR PROCESS */}
      <section className="wp-process" ref={processRef}>
        <div className="wp-process-bg" />
        <div className="wp-container wp-process-inner">
          <div className="wp-section-eyebrow wp-section-eyebrow--light">
            <span />
            <span>Our Process</span>
            <span />
          </div>
          <h2 className="wp-process-h2">
            From First Call to
            <br />
            <em className="wp-em-light">Lasting Wealth</em>
          </h2>
          <p className="wp-process-sub">
            A straightforward four-step journey — transparent, structured, and
            built entirely around you.
          </p>
          <div className="wp-steps">
            {process.map((s, i) => (
              <ProcessStep
                key={s.step}
                {...s}
                delay={i * 100}
                inView={processInView}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="wp-cta" ref={ctaRef}>
        <div className="wp-container">
          <div className={`wp-cta-card ${ctaInView ? "wp-cta-card--in" : ""}`}>
            <div className="wp-cta-glow" />
            <div className="wp-cta-dots" />
            <div className="wp-cta-content">
              <div className="wp-cta-eyebrow">
                <i className="fas fa-calendar-check" /> Free Consultation
              </div>
              <h2 className="wp-cta-h2">
                Start Multiplying
                <br />
                Your Wealth Today
              </h2>
              <p className="wp-cta-sub">
                Book a free 30-minute call with our advisors. No commitment, no
                jargon — just clear, honest guidance on the best path forward
                for your money.
              </p>
              <div className="wp-cta-actions">
                <button
                  className="wp-cta-btn-primary"
                  onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.WEALTH))}
                >
                  Book Free Consultation <i className="fas fa-arrow-right" />
                </button>
                <button
                  className="wp-cta-btn-ghost"
                  onClick={() => navigate("/contact")}
                >
                  <i className="fas fa-phone-alt" /> Talk to an Advisor
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WealthPage;
